/**
 * Landing Page - Apple-Inspired Premium Design
 * =============================================
 * Modern, impactful design with clean white background
 * Inspired by Apple MacBook Pro page aesthetics
 * 
 * REFACTORED: Uses extracted components from /components/landing/
 */

import React, { useEffect, useRef, useState, lazy, Suspense } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { openDemoModal, openWaitlistModal } from '@/components/leadCapture';
import {
  ArrowRight, Play, ChevronRight, Check, Star,
  BarChart3, Zap, Shield, Globe, Users, TrendingUp,
  Brain, Database, Target, Sparkles, Building2, Activity, ChevronDown, Lock, EyeOff
} from 'lucide-react';

// Below-the-fold body is one lazy chunk (keeps it out of main.js -> lower TBT).
const LandingBelowFold = lazy(() => import('@/components/landing/LandingBelowFold'));
import PublishedTestimonials from '@/components/landing/PublishedTestimonials';
import LiveWarehouseTeaser from '@/components/landing/LiveWarehouseTeaser';
import WaitlistPopup from '@/components/WaitlistPopup';


// Import extracted shared components
import { 
  AnimatedSection, 
  BentoCard, 
  FeatureIcon, 
  StatItem,
  useCounter,
  fadeUp,
  stagger 
} from '@/components/landing/LandingShared';

// Interactive Electrical Particle System with Mouse Follow Effect
const ElectricalParticles = ({ count = 70, mousePosition }) => {
  // Create varied particles with different sizes and behaviors
  const particles = React.useMemo(() => 
    Array.from({ length: count }, (_, i) => ({
      id: i,
      size: i < 20 ? Math.random() * 12 + 6 : Math.random() * 7 + 3,
      baseX: 10 + Math.random() * 80,
      baseY: 10 + Math.random() * 80,
      delay: Math.random() * 2,
      duration: Math.random() * 3 + 2.5,
      glowIntensity: i < 20 ? Math.random() * 0.4 + 0.6 : Math.random() * 0.4 + 0.3,
      isCore: i < 20,
      mouseInfluence: Math.random() * 0.3 + 0.1, // How much mouse affects this particle
    })), [count]);

  return (
    <div className="absolute inset-0 pointer-events-none" style={{ overflow: 'visible' }}>
      {particles.map((particle) => {
        // Calculate mouse influence on position
        const mouseOffsetX = mousePosition ? (mousePosition.x - 50) * particle.mouseInfluence : 0;
        const mouseOffsetY = mousePosition ? (mousePosition.y - 50) * particle.mouseInfluence : 0;
        
        return (
          <motion.div
            key={particle.id}
            className="absolute rounded-full"
            style={{
              width: particle.size,
              height: particle.size,
              left: `${particle.baseX}%`,
              top: `${particle.baseY}%`,
              background: particle.isCore 
                ? `radial-gradient(circle, rgba(34, 211, 238, 0.95) 0%, rgba(6, 182, 212, 0.7) 30%, rgba(14, 165, 233, 0.3) 60%, transparent 80%)`
                : `radial-gradient(circle, rgba(34, 211, 238, ${particle.glowIntensity}) 0%, rgba(6, 182, 212, ${particle.glowIntensity * 0.5}) 50%, transparent 80%)`,
              boxShadow: particle.isCore
                ? `0 0 ${particle.size * 3}px rgba(34, 211, 238, 0.9), 0 0 ${particle.size * 6}px rgba(6, 182, 212, 0.5), 0 0 ${particle.size * 9}px rgba(14, 165, 233, 0.3)`
                : `0 0 ${particle.size * 2}px rgba(34, 211, 238, ${particle.glowIntensity * 0.7}), 0 0 ${particle.size * 4}px rgba(6, 182, 212, ${particle.glowIntensity * 0.3})`,
            }}
            animate={{
              x: [mouseOffsetX, mouseOffsetX + Math.random() * 40 - 20, mouseOffsetX + Math.random() * 30 - 15, mouseOffsetX],
              y: [mouseOffsetY, mouseOffsetY + Math.random() * -50 - 15, mouseOffsetY + Math.random() * 40 - 20, mouseOffsetY],
              scale: particle.isCore ? [1, 1.8, 0.9, 1] : [1, 1.4, 0.7, 1],
              opacity: particle.isCore ? [0.7, 1, 0.8, 0.7] : [0.3, 0.9, 0.5, 0.3],
            }}
            transition={{
              duration: particle.duration,
              delay: particle.delay,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        );
      })}
      {/* Electric arc/spark effects */}
      {[...Array(12)].map((_, i) => (
        <motion.div
          key={`arc-${i}`}
          className="absolute"
          style={{
            width: 2,
            left: `${15 + i * 6}%`,
            top: `${15 + (i % 6) * 12}%`,
            height: `${25 + (i % 4) * 12}px`,
            background: 'linear-gradient(to bottom, rgba(34, 211, 238, 0.95), rgba(6, 182, 212, 0.6), transparent)',
            borderRadius: '1px',
            transform: `rotate(${-40 + i * 10}deg)`,
            boxShadow: '0 0 8px rgba(34, 211, 238, 0.9)',
          }}
          animate={{
            opacity: [0, 1, 0],
            scaleY: [0.2, 1, 0.2],
          }}
          transition={{
            duration: 0.4 + (i % 3) * 0.15,
            delay: i * 0.15,
            repeat: Infinity,
            repeatDelay: 1 + (i % 5) * 0.4,
          }}
        />
      ))}
    </div>
  );
};

// GPU-composited, CSS-only particle field for mobile. Animates transform/opacity
// (compositor thread) instead of framer-motion (main thread), so it keeps the
// glowing-dots aesthetic at a fraction of the cost — no JS runs per frame.
const MobileParticles = ({ count = 14 }) => {
  const dots = React.useMemo(
    () =>
      Array.from({ length: count }, (_, i) => ({
        id: i,
        size: i < 4 ? Math.random() * 8 + 9 : Math.random() * 5 + 4,
        left: 8 + Math.random() * 84,
        top: 10 + Math.random() * 78,
        delay: (Math.random() * 4).toFixed(2),
        dur: (Math.random() * 3 + 4).toFixed(2),
        core: i < 4,
      })),
    [count]
  );
  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
      {dots.map((d) => (
        <span
          key={d.id}
          className="cv-mobile-particle"
          style={{
            width: d.size,
            height: d.size,
            left: `${d.left}%`,
            top: `${d.top}%`,
            '--delay': `${d.delay}s`,
            '--dur': `${d.dur}s`,
            '--o': d.core ? 0.85 : 0.5,
          }}
        />
      ))}
    </div>
  );
};

// Floating 3D Orb Component
const Floating3DOrb = ({ size = 80, delay = 0, position }) => (
  <motion.div
    className="absolute rounded-full"
    style={{
      width: size,
      height: size,
      ...position,
      background: `
        radial-gradient(circle at 30% 30%, 
          rgba(255,255,255,0.8) 0%, 
          rgba(34, 211, 238, 0.4) 20%, 
          rgba(6, 182, 212, 0.3) 40%, 
          rgba(14, 165, 233, 0.2) 60%, 
          transparent 80%
        )
      `,
      boxShadow: `
        inset 0 0 ${size/4}px rgba(255,255,255,0.5),
        0 0 ${size/2}px rgba(34, 211, 238, 0.3),
        0 0 ${size}px rgba(6, 182, 212, 0.2)
      `,
      backdropFilter: 'blur(2px)',
    }}
    animate={{
      y: [0, -20, 0],
      x: [0, 10, 0],
      scale: [1, 1.05, 1],
    }}
    transition={{
      duration: 6,
      delay,
      repeat: Infinity,
      ease: "easeInOut",
    }}
  />
);
export default function LandingPagePremium() {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const logoContainerRef = useRef(null);
  const [mousePosition, setMousePosition] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  // Perf: choose the hero effect by device. Desktop keeps the rich framer-motion
  // particle/spark field. Mobile gets a GPU-composited CSS-only field (transform/
  // opacity → compositor thread, not the main thread), so it still looks great
  // without spiking Total Blocking Time. Reduced-motion users get none.
  const [fxMode, setFxMode] = useState('none');
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { setFxMode('none'); return; }
    setFxMode(window.matchMedia('(min-width: 768px)').matches ? 'desktop' : 'mobile');
  }, []);
  
  // Removed aggressive scroll fade - hero content stays visible longer
  // Only subtle parallax effect for polish without disrupting readability

  // Mouse tracking for particle effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (logoContainerRef.current) {
        const rect = logoContainerRef.current.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        setMousePosition({ x, y });
      }
    };

    const container = logoContainerRef.current;
    if (container) {
      container.addEventListener('mousemove', handleMouseMove);
      container.addEventListener('mouseleave', () => setMousePosition(null));
    }

    return () => {
      if (container) {
        container.removeEventListener('mousemove', handleMouseMove);
        container.removeEventListener('mouseleave', () => setMousePosition(null));
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page-premium">
      <WaitlistPopup />
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Corvana | AI Business Intelligence Platform Australia</title>
        <meta name="description" content="Corvana is Australia's AI-powered business intelligence platform: real-time analytics, 160+ integrations and predictive forecasting for Australian SMEs." />
        <meta name="keywords" content="business intelligence Australia, AI analytics platform, real-time dashboard, Xero integration, MYOB integration, SME analytics, predictive analytics, business KPI software" />
        <link rel="canonical" href="https://corvana.com.au" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Corvana | AI Business Intelligence Platform Australia" />
        <meta property="og:description" content="Real-time business analytics with 160+ integrations. AI-powered insights, compliance automation, and business valuation for Australian SMEs." />
        <meta property="og:url" content="https://corvana.com.au" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://corvana.com.au/og-image.png?v=4" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Corvana | AI Business Intelligence Platform" />
        <meta name="twitter:description" content="Australia's AI-powered business intelligence. Real-time analytics, 160+ integrations, predictive forecasting." />
        <meta name="twitter:image" content="https://corvana.com.au/og-image.png?v=4" />
        
        {/* Additional SEO */}
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large" />
        <meta name="geo.region" content="AU" />
        <meta name="geo.placename" content="Sydney, Australia" />
      </Helmet>
      {/* ========================================
          HERO SECTION
          Clean, bold, Apple-inspired with Corvana Logo
          ======================================== */}
      <section 
        ref={heroRef}
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        {/* Subtle background gradient orbs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 -left-20 w-[300px] md:w-[600px] h-[300px] md:h-[600px] bg-gradient-to-r from-sky-200/30 to-cyan-200/30 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 -right-20 w-[250px] md:w-[500px] h-[250px] md:h-[500px] bg-gradient-to-r from-violet-200/20 to-purple-200/20 rounded-full blur-3xl" />
        </div>
        
        <motion.div 
          initial={false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24 text-center pt-24 md:pt-32"
        >
          {/* Corvana Logo - Front and center on ALL devices */}
          <motion.div
            ref={logoContainerRef}
            initial={false}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, ease: [0.2, 0.65, 0.3, 0.9] }}
            className="relative mb-4 md:mb-2 mx-auto cursor-pointer"
            style={{ width: '100%', maxWidth: '1200px' }}
          >
            {/* Electrical particle container - scaled for mobile */}
            <div 
              className="absolute pointer-events-none"
              style={{ 
                top: '-40px', 
                left: '-60px', 
                right: '-60px', 
                bottom: '-40px',
                overflow: 'visible'
              }}
            >
              {fxMode === 'desktop' && <ElectricalParticles count={40} mousePosition={mousePosition} />}
              {fxMode === 'mobile' && <MobileParticles count={14} />}
            </div>
            
            {/* Glow layers - scaled for mobile */}
            <div className="absolute inset-0 -m-12 md:-m-32 bg-gradient-radial from-[#4DA6FF]/35 via-cyan-300/20 to-transparent blur-3xl" />
            <div className="absolute inset-0 -m-6 md:-m-16 bg-gradient-radial from-sky-300/30 via-transparent to-transparent blur-2xl" />
            <div className="absolute inset-0 -m-3 md:-m-8 bg-gradient-radial from-cyan-200/20 via-transparent to-transparent blur-xl" />
            
            {/* Logo - Responsive sizing - Smaller on mobile to emphasize headline */}
            <img 
              src="/corvana-hero-logo-v2.webp"
              sizes="(min-width: 1024px) 520px, (min-width: 768px) 420px, (min-width: 640px) 320px, 240px"
              alt="Corvana"
              width="891"
              height="599"
              fetchPriority="high"
              loading="eager"
              decoding="async"
              className="relative z-10 w-auto object-contain mx-auto max-w-[240px] sm:max-w-[320px] md:max-w-[420px] lg:max-w-[520px]"
              data-testid="hero-logo"
              style={{ 
                filter: 'drop-shadow(0 12px 40px rgba(6, 182, 212, 0.25)) drop-shadow(0 4px 15px rgba(0,0,0,0.1))'
              }}
            />
          </motion.div>
          
          {/* Announcement Badge - Subtle accent, not competing with headline */}
          <motion.div
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mb-2 md:mb-6"
          >
            <Link to="/about" aria-label="Learn about how Corvana was built">
              <Badge 
                className="px-2.5 py-1 md:px-5 md:py-2.5 rounded-full bg-slate-900/80 text-white text-[9px] md:text-sm font-medium hover:bg-slate-800 cursor-pointer transition-colors inline-flex items-center"
                data-testid="hero-badge"
              >
                <Sparkles className="w-2 h-2 md:w-4 md:h-4 mr-1 md:mr-2 flex-shrink-0" />
                <span className="whitespace-nowrap">Built from the ground up using AI</span>
                <ChevronRight className="w-2 h-2 md:w-4 md:h-4 ml-0.5 md:ml-1 flex-shrink-0" />
              </Badge>
            </Link>
          </motion.div>
          
          {/* Main Headline - MASSIVE and dominant on ALL devices */}
          <motion.h1 
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-display font-bold tracking-tight leading-[0.88] relative px-1"
            style={{ 
              fontSize: 'clamp(2.8rem, 11vw, 6rem)'
            }}
          >
            <span className="shimmer-text-enhanced block md:inline">Business Intelligence.</span>
            <br className="hidden md:block" />
            <span className="shimmer-text-enhanced block md:inline" style={{ animationDelay: '0.5s' }}>Reimagined.</span>
          </motion.h1>
          
          {/* Subtitle - Scaled appropriately for all devices */}
          <motion.p 
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mt-4 md:mt-6 text-base md:text-xl lg:text-2xl text-slate-600 max-w-3xl mx-auto leading-relaxed px-2 md:px-0"
          >
            AI-powered analytics that transform how Australian businesses 
            understand performance, predict trends, and make decisions.
          </motion.p>
          
          {/* CTAs - Responsive sizing */}
          <motion.div 
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0 }}
            className="mt-6 md:mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4 px-4 md:px-0"
          >
            <Link to="/get-started" className="relative w-full sm:w-auto group">
              <span className="absolute -inset-1 rounded-full bg-gradient-to-r from-sky-500 via-cyan-400 to-sky-500 opacity-60 blur-lg group-hover:opacity-90 transition-opacity duration-300 animate-pulse pointer-events-none" aria-hidden="true" />
              <Button 
                size="lg"
                className="relative h-14 md:h-16 px-8 md:px-12 rounded-full bg-gradient-to-r from-sky-600 to-cyan-500 hover:from-sky-500 hover:to-cyan-400 text-white font-bold text-lg md:text-xl tracking-tight transition-all hover:scale-[1.04] active:scale-95 shadow-2xl shadow-sky-500/40 w-full sm:w-auto"
                data-testid="hero-cta-primary" onClick={() => navigate('/get-started')}
              >Get Started Free
                <ArrowRight className="w-5 h-5 md:w-6 md:h-6 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Button 
              variant="outline"
              size="lg"
              className="h-12 md:h-14 px-6 md:px-8 rounded-full text-slate-800 border-slate-300 font-medium text-base md:text-lg hover:bg-slate-100 transition-all group w-full sm:w-auto"
              data-testid="hero-cta-demo"
              onClick={openDemoModal}
            >
              Book a Demo
              <ArrowRight className="w-4 h-4 md:w-5 md:h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </motion.div>

          {/* Corvana Brief discovery link */}
          <motion.div
            initial={false}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
            className="mt-5 md:mt-7"
          >
            <Link
              to="/brief"
              data-testid="hero-brief-link"
              className="inline-flex items-center gap-2 text-sm md:text-base text-slate-500 hover:text-[#007AFF] transition-colors group"
            >
              <Sparkles className="w-4 h-4 text-[#007AFF]" />
              <span>Get the free <span className="font-semibold text-slate-700 group-hover:text-[#007AFF]">Corvana Brief</span> — personalised AI business news</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>

          <LiveWarehouseTeaser />
          
        </motion.div>
      </section>

      <Suspense fallback={<div style={{ minHeight: 600 }} aria-hidden="true" />}>
        <LandingBelowFold />
      </Suspense>
      <PublishedTestimonials />
    </div>
  );
}
